import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { toast } from "react-toastify";
import {
  getSessions,
  getGrades,
  getSubjectsByGrade,
  getStudentsByGrade,
  getAttendances,
  saveAttendance,
} from "../../api/attendanceApi";

const STATUS_OPTIONS = ["present", "absent", "leave", "late", "holiday"];

const AttendanceForm = ({ sessionId, gradeId, subjectId, date, onClose, onSaved }) => {
  const [sessions, setSessions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedSessionId, setSelectedSessionId] = useState(sessionId || null);
  const [selectedGradeId, setSelectedGradeId] = useState(gradeId || null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(subjectId || null);
  const [selectedDate, setSelectedDate] = useState(date || new Date());

  // 🔹 Load sessions
  useEffect(() => {
    getSessions()
      .then((res) => {
        const all = Array.isArray(res) ? res : res?.data || [];
        const active = all.filter(
          (s) => s.status?.toLowerCase() === "active" || s.is_active === true
        );
        setSessions(active);
      })
      .catch(() => toast.error("Failed to load sessions"));
  }, []);

  // 🔹 Load grades when session changes
  useEffect(() => {
    if (!selectedSessionId) {
      setGrades([]);
      setSelectedGradeId(null);
      return;
    }
    getGrades(selectedSessionId)
      .then((res) => setGrades(Array.isArray(res) ? res : res?.data || []))
      .catch(() => toast.error("Failed to load grades"));
  }, [selectedSessionId]);

  // 🔹 Load subjects and students
  useEffect(() => {
    const load = async () => {
      if (!selectedGradeId) {
        setSubjects([]);
        setStudents([]);
        return;
      }
      try {
        const subj = await getSubjectsByGrade(selectedGradeId);
        setSubjects(Array.isArray(subj) ? subj : subj?.data || []);

        const studentsData = await getStudentsByGrade(selectedGradeId);
        const sd = Array.isArray(studentsData) ? studentsData : studentsData?.data || [];

        setStudents(sd.map((s) => ({ id: s.id, full_name: s.full_name, status: "present" })));
      } catch (err) {
        console.error(err);
        toast.error("Failed to load form data");
      }
    };
    load();
  }, [selectedGradeId, selectedSubjectId, selectedSessionId, selectedDate]);

  const handleStatusChange = (studentId, status) => {
    setStudents((prev) => prev.map((s) => (s.id === studentId ? { ...s, status } : s)));
  };

  // 🔹 Save Attendance Only (No Update)
  const handleSave = async () => {
    if (!selectedSessionId || !selectedGradeId || !selectedSubjectId || !selectedDate) {
      toast.error("Select session, grade, subject and date");
      return;
    }

    try {
      const payload = {
        sessionId: selectedSessionId,
        gradeId: selectedGradeId,
        subjectId: selectedSubjectId,
        date: selectedDate.toISOString().split("T")[0],
        records: students.map((s) => ({ studentId: s.id, status: s.status })),
      };

      const res = await saveAttendance(payload);

      if (res.success) {
        toast.success("Attendance saved successfully");
        onSaved(students.map((s) => ({ studentId: s.id, status: s.status })));
        onClose();
      } else {
        toast.error(res.message || "Failed to save attendance");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving attendance");
    }
  };

  return (
    <Dialog
      header="Mark Attendance"
      visible
      style={{ width: "60vw" }}
      onHide={onClose}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label="Cancel" className="p-button-text" onClick={onClose} />
          <Button label="Save Attendance" icon="pi pi-check" onClick={handleSave} />
        </div>
      }
    >
      <div className="grid p-fluid mb-4">
        <div className="col-6">
          <label>Session</label>
          <Dropdown
            value={selectedSessionId}
            options={sessions}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Session"
            onChange={(e) => setSelectedSessionId(e.value)}
          />
        </div>

        <div className="col-6">
          <label>Grade</label>
          <Dropdown
            value={selectedGradeId}
            options={grades}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Grade"
            onChange={(e) => setSelectedGradeId(e.value)}
          />
        </div>

        <div className="col-6">
          <label>Subject</label>
          <Dropdown
            value={selectedSubjectId}
            options={subjects}
            optionLabel="name"
            optionValue="id"
            placeholder="Select Subject"
            onChange={(e) => setSelectedSubjectId(e.value)}
          />
        </div>

        <div className="col-6">
          <label>Date</label>
          <Calendar
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.value ? new Date(e.value) : new Date())}
            dateFormat="yy-mm-dd"
          />
        </div>
      </div>

      <Card className="shadow-3 border-round-lg">
        <table className="attendance-table w-full">
          <thead>
            <tr>
              <th>Student Name</th>
              <th className="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan={2} className="p-4 text-center">
                  No students found for the selected grade.
                </td>
              </tr>
            ) : (
              students.map((s, index) => (
                <tr key={s.id}>
                  <td>{s.full_name}</td>
                  <td className="text-center">
                    <div className="flex justify-content-center gap-3 align-items-center">
                      {STATUS_OPTIONS.map((status) => (
                        <label key={status} className="flex align-items-center gap-1">
                          <input
                            type="radio"
                            name={`status_${index}`}
                            value={status}
                            checked={String(s.status || "").toLowerCase() === status.toLowerCase()}
                            onChange={() => handleStatusChange(s.id, status)}
                          />
                          <span>{status}</span>
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </Dialog>
  );
};

export default AttendanceForm;
